import { useEffect, useRef } from "preact/hooks";
import { t } from "../i18n";
import { getGoogleClientId, loginWithGoogle } from "../app/auth";

interface GsiCredential {
  credential?: string;
}

interface GsiIdApi {
  initialize: (config: {
    client_id: string;
    callback: (response: GsiCredential) => void;
    ux_mode?: string;
    auto_select?: boolean;
    cancel_on_tap_outside?: boolean;
  }) => void;
  renderButton: (
    parent: HTMLElement,
    options: {
      type: string;
      theme: string;
      size: string;
      text: string;
      shape: string;
      width?: number;
      logo_alignment?: string;
    },
  ) => void;
}

declare global {
  interface Window {
    google?: { accounts?: { id?: GsiIdApi } };
  }
}

let gsiLoading: Promise<void> | null = null;

function loadGsi(): Promise<void> {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (gsiLoading) return gsiLoading;
  gsiLoading = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error(t("account.google.setup"))), { once: true });
      return;
    }
    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      gsiLoading = null;
      reject(new Error(t("account.google.setup")));
    };
    document.head.appendChild(script);
  });
  return gsiLoading;
}

export function GoogleContinue({
  consent,
  requireConsent,
  onError,
  onSignedIn,
  disabled,
}: {
  consent: boolean;
  requireConsent: boolean;
  onError: (message: string) => void;
  onSignedIn?: () => void;
  disabled?: boolean;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const consentRef = useRef(consent);
  const onErrorRef = useRef(onError);
  const onSignedInRef = useRef(onSignedIn);
  const clientId = getGoogleClientId();

  consentRef.current = consent;
  onErrorRef.current = onError;
  onSignedInRef.current = onSignedIn;

  useEffect(() => {
    const host = hostRef.current;
    if (!clientId || !host) return;
    let gone = false;
    host.replaceChildren();
    loadGsi()
      .then(() => {
        const api = window.google?.accounts?.id;
        if (gone || !api || !hostRef.current) return;
        api.initialize({
          client_id: clientId,
          ux_mode: "popup",
          auto_select: false,
          cancel_on_tap_outside: true,
          callback: (response) => {
            if (requireConsent && !consentRef.current) {
              onErrorRef.current(t("account.consent.required"));
              return;
            }
            const token = response.credential ?? "";
            if (!token) {
              onErrorRef.current(t("account.google.cancelled"));
              return;
            }
            void loginWithGoogle(token, consentRef.current)
              .then(() => {
                onSignedInRef.current?.();
              })
              .catch((err: unknown) => {
                onErrorRef.current(err instanceof Error ? err.message : t("account.error"));
              });
          },
        });
        api.renderButton(hostRef.current, {
          type: "standard",
          theme: "outline",
          size: "large",
          text: "continue_with",
          shape: "rectangular",
          width: 320,
          logo_alignment: "left",
        });
      })
      .catch((err: unknown) => {
        if (!gone) onErrorRef.current(err instanceof Error ? err.message : t("account.google.setup"));
      });
    return () => {
      gone = true;
      host.replaceChildren();
    };
  }, [clientId, requireConsent]);

  const onFallback = () => {
    if (requireConsent && !consent) {
      onError(t("account.consent.required"));
      return;
    }
    onError(t("account.google.setup"));
  };

  return (
    <div class="obs-google">
      {clientId ? (
        <div class="obs-google-host" ref={hostRef} />
      ) : (
        <button type="button" class="obs-btn obs-btn-google" onClick={onFallback} disabled={disabled}>
          <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
            <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z" />
            <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.71H.96v2.33A9 9 0 0 0 9 18z" />
            <path fill="#FBBC05" d="M3.97 10.71A5.41 5.41 0 0 1 3.69 9c0-.59.1-1.17.26-1.71V4.96H.96A9 9 0 0 0 0 9c0 1.45.35 2.82.96 4.04l3.01-2.33z" />
            <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.96l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z" />
          </svg>
          {t("account.google")}
        </button>
      )}
      <p class="obs-google-or">{t("account.google.or")}</p>
    </div>
  );
}
